import {FC, useEffect, useState} from 'react'
import {api, ApiError, CleanerAccount, ConnectSummary} from '../../services/api'
import {Spinner} from '../../components/Spinner'
import {PageHead} from '../../components/PageHead'
import {CleanerAdminLedger} from './CleanerAdminLedger'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 13px',
  border: '1px solid #e5e7eb',
  borderRadius: 9,
  fontSize: 14,
  background: '#fff',
  boxSizing: 'border-box',
  outline: 'none',
}

const Card: FC<{title: string; sub?: string; children: React.ReactNode}> = ({title, sub, children}) => (
  <div className='ccp-lift' style={{background: '#fff', borderRadius: 16, boxShadow: '0 6px 18px rgba(27,27,41,0.06)', border: '1px solid #eef0f4', padding: '24px 26px', marginBottom: 18}}>
    <h2 style={{fontSize: 18, fontWeight: 800, color: INK, margin: 0}}>{title}</h2>
    {sub && <p style={{fontSize: 13.5, color: MUTED, margin: '4px 0 18px'}}>{sub}</p>}
    {!sub && <div style={{height: 16}} />}
    {children}
  </div>
)

const Notice: FC<{kind: 'ok' | 'err'; children: React.ReactNode}> = ({kind, children}) => (
  <div style={{borderRadius: 9, padding: '10px 14px', fontSize: 14, marginBottom: 16, ...(kind === 'ok' ? {background: 'rgba(3,149,64,0.1)', color: '#1f9d5b'} : {background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626'})}}>
    {children}
  </div>
)

const Label: FC<{children: React.ReactNode}> = ({children}) => (
  <label style={{display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6}}>{children}</label>
)

const btn = (disabled: boolean): React.CSSProperties => ({
  fontWeight: 700, fontSize: 15, color: '#fff', background: ACCENT, border: 'none',
  borderRadius: 10, padding: '12px 26px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.7 : 1,
  display: 'inline-flex', alignItems: 'center', gap: 8,
})

const CONNECT_BADGE: Record<CleanerAccount['connectStatus'], {label: string; bg: string; color: string}> = {
  NONE: {label: 'Not set up', bg: '#f1f3f7', color: '#7e8299'},
  PENDING: {label: 'Onboarding started', bg: 'rgba(255,168,0,0.15)', color: '#b8770a'},
  VERIFIED: {label: 'Verified — payouts ready', bg: 'rgba(3,149,64,0.12)', color: '#1f9d5b'},
}

const CleanerAdminPage: FC = () => {
  const [loading, setLoading] = useState(true)
  const [account, setAccount] = useState<CleanerAccount | null>(null)

  useEffect(() => {
    api
      .getCleanerAccount()
      .then(setAccount)
      .catch(() => setAccount({cleaner: null, connectStatus: 'NONE'}))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div>
        <PageHead icon='🧹' eyebrow='Housekeeping' title='Cleaner' sub='Manage the cleaner’s login and details.' />
        <div style={{padding: 40, textAlign: 'center', color: MUTED}}>Loading…</div>
      </div>
    )
  }

  return (
    <div>
      <PageHead
        icon='🧹'
        eyebrow='Housekeeping'
        title='Cleaner'
        sub='Manage the cleaner’s login and contact details. She uses these to sign in to her housekeeping portal.'
      />
      <div className='ccp-stagger'>
        {account?.cleaner ? (
          <>
            <CleanerAdminLedger />
            <ExistingCleaner account={account} onChange={setAccount} />
          </>
        ) : (
          <CreateCleaner onCreated={setAccount} />
        )}
      </div>
    </div>
  )
}

// ─── No cleaner yet — create the login ────────────────────────────────────
const CreateCleaner: FC<{onCreated: (a: CleanerAccount) => void}> = ({onCreated}) => {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<{kind: 'ok' | 'err'; text: string} | null>(null)

  const create = async () => {
    setMsg(null)
    if (!firstName.trim() || !lastName.trim()) return setMsg({kind: 'err', text: 'First and last name are required.'})
    if (!email.trim()) return setMsg({kind: 'err', text: 'Email is required.'})
    if (password.length < 8) return setMsg({kind: 'err', text: 'Password must be at least 8 characters.'})
    setSaving(true)
    try {
      const acc = await api.createCleaner({firstName, lastName, email: email.trim(), phone: phone.trim() || null, password})
      onCreated(acc)
    } catch (e) {
      setMsg({kind: 'err', text: e instanceof ApiError ? e.message : 'Could not create the cleaner account'})
      setSaving(false)
    }
  }

  return (
    <Card title='Create the cleaner login' sub='No cleaner account exists yet. Set one up so she can sign in to the housekeeping portal.'>
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      <div className='row g-3'>
        <div className='col-md-6'>
          <Label>First name</Label>
          <input style={inputStyle} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </div>
        <div className='col-md-6'>
          <Label>Last name</Label>
          <input style={inputStyle} value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
        <div className='col-md-6'>
          <Label>Email (she signs in with this)</Label>
          <input style={inputStyle} type='email' value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className='col-md-6'>
          <Label>Phone</Label>
          <input style={inputStyle} value={phone} placeholder='Optional' onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div className='col-md-6'>
          <Label>Temporary password</Label>
          <input style={inputStyle} type='text' value={password} placeholder='At least 8 characters' onChange={(e) => setPassword(e.target.value)} />
        </div>
      </div>
      <div style={{marginTop: 20}}>
        <button style={btn(saving)} disabled={saving} onClick={create}>
          {saving && <Spinner />} Create cleaner login
        </button>
      </div>
    </Card>
  )
}

// ─── Cleaner exists — edit details, reset password ────────────────────────
const ExistingCleaner: FC<{account: CleanerAccount; onChange: (a: CleanerAccount) => void}> = ({account, onChange}) => {
  const c = account.cleaner!
  const badge = CONNECT_BADGE[account.connectStatus]

  // Profile form
  const [firstName, setFirstName] = useState(c.firstName)
  const [lastName, setLastName] = useState(c.lastName)
  const [email, setEmail] = useState(c.email)
  const [phone, setPhone] = useState(c.phone ?? '')
  const [pSaving, setPSaving] = useState(false)
  const [pMsg, setPMsg] = useState<{kind: 'ok' | 'err'; text: string} | null>(null)

  // Password form
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [pwSaving, setPwSaving] = useState(false)
  const [pwMsg, setPwMsg] = useState<{kind: 'ok' | 'err'; text: string} | null>(null)

  const saveProfile = async () => {
    setPMsg(null)
    if (!firstName.trim() || !lastName.trim()) return setPMsg({kind: 'err', text: 'First and last name are required.'})
    if (!email.trim()) return setPMsg({kind: 'err', text: 'Email is required.'})
    setPSaving(true)
    try {
      const acc = await api.updateCleaner({firstName, lastName, email: email.trim(), phone: phone.trim() || null})
      onChange(acc)
      setPMsg({kind: 'ok', text: 'Cleaner details updated.'})
    } catch (e) {
      setPMsg({kind: 'err', text: e instanceof ApiError ? e.message : 'Could not save details'})
    } finally {
      setPSaving(false)
    }
  }

  const changePassword = async () => {
    setPwMsg(null)
    if (next.length < 8) return setPwMsg({kind: 'err', text: 'New password must be at least 8 characters.'})
    if (next !== confirm) return setPwMsg({kind: 'err', text: 'Passwords do not match.'})
    setPwSaving(true)
    try {
      await api.setCleanerPassword(next)
      setNext('')
      setConfirm('')
      setPwMsg({kind: 'ok', text: 'Password reset. Share the new password with her.'})
    } catch (e) {
      setPwMsg({kind: 'err', text: e instanceof ApiError ? e.message : 'Could not reset password'})
    } finally {
      setPwSaving(false)
    }
  }

  return (
    <>
      <PayoutSetup initialStatus={account.connectStatus} />
      <Card title='Account details' sub='Her name, sign-in email and phone. Changing the email changes how she signs in.'>
        <div style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap'}}>
          <span style={{fontSize: 13, color: MUTED}}>Payout account:</span>
          <span style={{fontSize: 12, fontWeight: 700, borderRadius: 999, padding: '4px 11px', background: badge.bg, color: badge.color}}>{badge.label}</span>
        </div>
        {pMsg && <Notice kind={pMsg.kind}>{pMsg.text}</Notice>}
        <div className='row g-3'>
          <div className='col-md-6'>
            <Label>First name</Label>
            <input style={inputStyle} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div className='col-md-6'>
            <Label>Last name</Label>
            <input style={inputStyle} value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
          <div className='col-md-6'>
            <Label>Email (sign-in)</Label>
            <input style={inputStyle} type='email' value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className='col-md-6'>
            <Label>Phone</Label>
            <input style={inputStyle} value={phone} placeholder='Optional' onChange={(e) => setPhone(e.target.value)} />
          </div>
        </div>
        <div style={{marginTop: 20}}>
          <button style={btn(pSaving)} disabled={pSaving} onClick={saveProfile}>
            {pSaving && <Spinner />} Save details
          </button>
        </div>
      </Card>

      <Card title='Reset password' sub='Set a new password for her and share it. She can’t reset it herself.'>
        {pwMsg && <Notice kind={pwMsg.kind}>{pwMsg.text}</Notice>}
        <div className='row g-3'>
          <div className='col-md-6'>
            <Label>New password</Label>
            <input style={inputStyle} type='text' value={next} placeholder='At least 8 characters' onChange={(e) => setNext(e.target.value)} />
          </div>
          <div className='col-md-6'>
            <Label>Confirm password</Label>
            <input style={inputStyle} type='text' value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
        </div>
        <div style={{marginTop: 20}}>
          <button style={btn(pwSaving)} disabled={pwSaving} onClick={changePassword}>
            {pwSaving && <Spinner />} Reset password
          </button>
        </div>
      </Card>
    </>
  )
}

// ─── Stripe payout onboarding ─────────────────────────────────────────────
const PayoutSetup: FC<{initialStatus: CleanerAccount['connectStatus']}> = ({initialStatus}) => {
  const [status, setStatus] = useState(initialStatus)
  const [link, setLink] = useState<string | null>(null)
  const [busy, setBusy] = useState<'link' | 'refresh' | 'delete' | null>(null)
  const [copied, setCopied] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [msg, setMsg] = useState<{kind: 'ok' | 'err'; text: string} | null>(null)
  const [summary, setSummary] = useState<ConnectSummary | null>(null)
  const badge = CONNECT_BADGE[status]

  useEffect(() => {
    if (status === 'VERIFIED') api.cleanerConnectSummary().then(setSummary).catch(() => {})
  }, [status])

  const getLink = async () => {
    setBusy('link'); setMsg(null)
    try {
      const {url} = await api.cleanerConnectLink()
      setLink(url)
      setStatus((s) => (s === 'NONE' ? 'PENDING' : s))
    } catch (e) {
      setMsg({kind: 'err', text: e instanceof ApiError ? e.message : 'Could not create the setup link. Is Stripe configured in Payment Settings?'})
    } finally {
      setBusy(null)
    }
  }

  const refresh = async () => {
    setBusy('refresh'); setMsg(null)
    try {
      const {status: s} = await api.cleanerConnectRefresh()
      setStatus(s)
      setMsg({kind: 'ok', text: s === 'VERIFIED' ? 'Verified — she’s ready to be paid.' : 'Not verified yet. She may still need to finish the Stripe steps.'})
    } catch (e) {
      setMsg({kind: 'err', text: e instanceof ApiError ? e.message : 'Could not refresh status'})
    } finally {
      setBusy(null)
    }
  }

  const del = async () => {
    setBusy('delete'); setMsg(null)
    try {
      const {status: s} = await api.cleanerConnectDelete()
      setStatus(s)
      setLink(null)
      setConfirmDelete(false)
      setMsg({kind: 'ok', text: 'Payout account removed — deleted at Stripe too. You can set her up again anytime.'})
    } catch (e) {
      setMsg({kind: 'err', text: e instanceof ApiError ? e.message : 'Could not remove the payout account.'})
    } finally {
      setBusy(null)
    }
  }

  const copy = () => {
    if (!link) return
    navigator.clipboard?.writeText(link).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <Card title='Payout setup (Stripe)' sub='One-time: she verifies her identity and bank at Stripe so you can pay her with one click. You never see her documents.'>
      <div style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap'}}>
        <span style={{fontSize: 13, color: MUTED}}>Status:</span>
        <span style={{fontSize: 12, fontWeight: 700, borderRadius: 999, padding: '4px 11px', background: badge.bg, color: badge.color}}>{badge.label}</span>
      </div>
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}

      {status !== 'VERIFIED' && (
        <>
          <div style={{display: 'flex', gap: 10, flexWrap: 'wrap'}}>
            <button style={btn(busy === 'link')} disabled={busy === 'link'} onClick={getLink}>
              {busy === 'link' && <Spinner />} {status === 'NONE' ? 'Create setup link' : 'Get a fresh link'}
            </button>
            {status !== 'NONE' && (
              <button style={{...btn(busy === 'refresh'), background: '#eef1f6', color: INK}} disabled={busy === 'refresh'} onClick={refresh}>
                {busy === 'refresh' && <Spinner color={INK} />} Refresh status
              </button>
            )}
          </div>

          {link && (
            <div style={{marginTop: 16, background: '#f8fafc', border: '1px solid #eef0f4', borderRadius: 10, padding: 14}}>
              <Label>Send her this link</Label>
              <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                <input readOnly value={link} style={{...inputStyle, flex: 1, minWidth: 220, fontSize: 13}} onFocus={(e) => e.target.select()} />
                <button onClick={copy} style={{...btn(false), padding: '11px 18px', background: copied ? '#1f9d5b' : ACCENT}}>{copied ? 'Copied!' : 'Copy'}</button>
                <a href={link} target='_blank' rel='noreferrer' style={{...btn(false), padding: '11px 18px', textDecoration: 'none', background: '#eef1f6', color: INK}}>Open</a>
              </div>
              <p style={{fontSize: 12.5, color: MUTED, margin: '10px 0 0'}}>
                The link is single-use and expires shortly. After she finishes, click “Refresh status” — it flips to Verified automatically once Stripe confirms her.
              </p>
            </div>
          )}
        </>
      )}

      {status === 'VERIFIED' && (
        <div>
          <p style={{fontSize: 14, color: '#1f9d5b', margin: 0, fontWeight: 600}}>✓ She’s verified and ready to be paid out.</p>
          <div style={{marginTop: 12, background: '#f8fafc', border: '1px solid #eef0f4', borderRadius: 10, padding: '12px 14px'}}>
            <Label>Payout bank</Label>
            {summary?.bankName || summary?.bankLast4 ? (
              <p style={{fontSize: 14, color: INK, margin: 0, fontWeight: 600}}>
                {summary?.bankName ?? 'Bank account'} ••••{summary?.bankLast4 ?? '····'}
                {summary?.routingNumber ? <span style={{color: MUTED, fontWeight: 400}}> · routing {summary.routingNumber}</span> : null}
              </p>
            ) : (
              <p style={{fontSize: 13, color: MUTED, margin: 0}}>On file securely with Stripe.</p>
            )}
            <p style={{fontSize: 12, color: MUTED, margin: '8px 0 0'}}>
              Her full bank number, SSN and ID stay with Stripe — never visible here.
            </p>
          </div>
          {summary?.dashboardUrl && (
            <a href={summary.dashboardUrl} target='_blank' rel='noreferrer'
               style={{display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12, fontSize: 13.5, fontWeight: 700, color: ACCENT, textDecoration: 'none'}}>
              View her payout details in Stripe ↗
            </a>
          )}
        </div>
      )}

      {status !== 'NONE' && (
        <div style={{marginTop: 20, paddingTop: 16, borderTop: '1px solid #eef0f4'}}>
          {!confirmDelete ? (
            <button
              onClick={() => {setConfirmDelete(true); setMsg(null)}}
              style={{background: 'none', border: 'none', color: '#c0392b', fontWeight: 600, fontSize: 13.5, cursor: 'pointer', padding: 0}}
            >
              Remove payout account
            </button>
          ) : (
            <div style={{background: '#fdf3f2', border: '1px solid #f4d6d3', borderRadius: 10, padding: 14}}>
              <p style={{fontSize: 13.5, color: INK, margin: '0 0 12px', lineHeight: 1.5}}>
                This <strong>permanently deletes her account at Stripe</strong> and clears her payout setup here. She’d have to verify her identity and bank again from scratch. Continue?
              </p>
              <div style={{display: 'flex', gap: 10, flexWrap: 'wrap'}}>
                <button style={{...btn(busy === 'delete'), background: '#c0392b'}} disabled={busy === 'delete'} onClick={del}>
                  {busy === 'delete' && <Spinner />} Yes, delete her account
                </button>
                <button style={{...btn(busy === 'delete'), background: '#eef1f6', color: INK}} disabled={busy === 'delete'} onClick={() => setConfirmDelete(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

export {CleanerAdminPage}
